/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { useStateWithEvent } from "../../../../../ui/hooks";
import { Panel, PanelNote } from "../../../../../ui/components/Panel";
import { ReadmeEditorInspectorItem } from "./ReadmeEditorInspectorItem";
import { ReadmeModel } from "../../../Common/ReadmeModel";

export interface IReadmeEditorInspectorProps {
	className?: string;
	readmeModel: ReadmeModel;
}

export const ReadmeEditorInspector: React.FC<IReadmeEditorInspectorProps> = ({ className, readmeModel }) => {
	const baseClass = BEM("edt-readme-editor-inspector");

	const selection = useMemo(() => readmeModel.getSelection(), [ readmeModel ]);

	const getNodeById = useCallback(
		(nodeId: number) => readmeModel.getDesignContext().getNodeById(nodeId),
		[ readmeModel ]
	);

	const selectedItem = useStateWithEvent(selection.onChange, () => selection.getItems()[0]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<Panel scroll mark="readme-inspector">
			{selectedItem ? (
				<ReadmeEditorInspectorItem selectionItem={selectedItem} getNodeById={getNodeById} />
			) : (
				<PanelNote>
					<p>Select a page to inspect.</p>
				</PanelNote>
			)}
		</Panel>
	);
};
